/**
 * WebMCP tools for the marketing site: what a browser agent (an AI acting in
 * the visitor's own browser) may call on any localized page.
 *
 * Phase 1 is read-only. Every tool returns what the site already publishes
 * for this market, and never acts for the visitor:
 *
 *   - `ahead_site_guide` reads the market's `llms.txt`, the map of the site.
 *   - `ahead_read_page` reads one page's `.md` mirror (lib/native-markdown),
 *     the same text AI crawlers get, so prices and availability are exactly
 *     what the page renders for this market.
 *   - `ahead_booking_link` returns the link the navbar's "Book check-up"
 *     button opens in this market (`mainBookingUrl`), or the waitlist where
 *     booking is not open. It
 *     preselects nothing, so it cannot offer what a market does not sell
 *     (Austria has no standalone blood test, Germany no MRI), and it does not
 *     navigate: the visitor chooses, books and pays in the booking app.
 *
 * Content comes from the mirrors on demand, so registering the tools costs
 * no payload on page load. The names carry an `ahead_` prefix so they never
 * read like the member connector's tools (ahead-iris-connector), which an
 * agent may hold at the same time.
 *
 * Spec: https://developer.chrome.com/docs/ai/webmcp/imperative-api (origin
 * trial, still changing). `components/webmcp/site-tools-registrar.tsx` is
 * the only place that touches the browser API.
 */

import { isWaitlistOnly, type Country } from "@/i18n/config"
import { bookingUrlForCountry, membershipBookingAppUrl } from "@/lib/booking"
import { isAllowedInCountry } from "@/lib/country-gates"
import type { BookingFlow } from "@/lib/membership-first"
import { externalMarkdownPathname } from "@/lib/markdown-paths"

/** A tool as `modelContext.registerTool` takes it. */
export interface SiteTool {
  name: string
  description: string
  inputSchema: {
    type: "object"
    properties: Record<string, unknown>
    required?: string[]
    additionalProperties?: boolean
  }
  annotations: { readOnlyHint: boolean }
  execute: (input: Record<string, unknown>) => Promise<string>
}

export interface SiteToolContext {
  country: Country
  /** URL language (`de`), not the message-file id. */
  locale: string
  flow: BookingFlow
  /** Same-origin GET of a site path, resolving to the response body. */
  fetchText: (path: string) => Promise<string>
}

/** A page mirror can run long (the biomarker index); an agent's context is
 *  not the place for all of it. */
export const MAX_PAGE_CHARS = 60_000

/**
 * The page path to read, as the `.md` mirror of a page in this market, or
 * null when the input names something else. Takes `/ch/en/pricing`,
 * `pricing`, or a full aheadhealth.com URL; refuses other markets, query
 * strings and any path the mirror does not serve.
 */
export function mirrorPathFor(
  input: string,
  country: string,
  locale: string
): string | null {
  const raw = input.trim()
  // Percent-escapes, backslashes, whitespace and control characters are how a
  // path smuggles dot-segments past a string check (`%2e%2e`, `.\t.`), and
  // no page path needs them: refuse them outright.
  if (!raw || /[%\\\s\u0000-\u001f\u007f]/.test(raw)) return null
  const absolute = /^https?:\/\//i.test(raw)
  if (!absolute && /[?#:]/.test(raw)) return null
  const prefix = `/${country}/${locale}`
  const candidate = absolute
    ? raw
    : raw.startsWith("/")
      ? raw
      : `${prefix}/${raw}`
  const url = new URL(candidate, "https://www.aheadhealth.com")
  if (!/(^|\.)aheadhealth\.com$/.test(url.hostname)) return null
  if (url.search || url.hash) return null
  // What the URL parser made of it is what fetch would request. Any
  // dot-segment it resolved means the input named a different path.
  const page = url.pathname.replace(/\/+$/, "")
  if (!absolute && page !== candidate.replace(/\/+$/, "")) return null
  if (page.split("/").includes("..") || page.split("/").includes(".")) {
    return null
  }
  if (page !== prefix && !page.startsWith(`${prefix}/`)) return null
  // The market home's mirror also answers at `/ch/en.md`, but `/de/de.md`
  // falls into next.config's legacy `/de/*` redirect; this form never does.
  if (page === prefix) return `${prefix}/index.html.md`
  return externalMarkdownPathname(page)
}

/** A booking link as an agent may hand it on: without the visitor's `?ph`
 *  hash (ours to forward between our own domains, not to give an outside
 *  agent), and with `utm_source=webmcp` so the booking app attributes it. */
export function agentBookingUrl(href: string): string {
  const url = new URL(href)
  url.searchParams.delete("ph")
  url.searchParams.set("utm_source", "webmcp")
  url.searchParams.set("utm_medium", "agent")
  return url.toString()
}

/**
 * The link the navbar's main "Book check-up" button opens on an ordinary page
 * of this market (components/layout/page-shell.tsx, without a saved basket,
 * an MRI page or a /membership page), as `agentBookingUrl` hands it on:
 * without the visitor's `?ph` hash, with `utm_source=webmcp`.
 */
export function mainBookingUrl(
  country: Country,
  locale: string,
  flow: BookingFlow
): string {
  const href = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
    ? membershipBookingAppUrl(country, locale, flow)
    : bookingUrlForCountry(
        country,
        locale,
        undefined,
        undefined,
        undefined,
        flow
      )
  return agentBookingUrl(href)
}

export function buildSiteTools(ctx: SiteToolContext): SiteTool[] {
  const { country, locale, flow, fetchText } = ctx
  const home = `/${country}/${locale}`
  const waitlistOnly = isWaitlistOnly(country)

  const siteGuide: SiteTool = {
    name: "ahead_site_guide",
    description:
      "Returns the guide to Ahead Health's website for the market the visitor is browsing: what Ahead offers in this market, the main pages with their paths, and FAQ topics. Use the paths it lists with ahead_read_page. For prices use ahead_get_offers; to find something specific use ahead_search.",
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    annotations: { readOnlyHint: true },
    execute: async () => fetchText(`${home}/llms.txt`),
  }

  const readPage: SiteTool = {
    name: "ahead_read_page",
    description: `Returns one page of Ahead Health's website as markdown, with this market's prices, locations and availability exactly as the page shows them. Pass a path from ahead_site_guide, such as "${home}/pricing", or a path relative to the market home, such as "pricing" or "faq". Only pages of the current market (${home}) can be read.`,
    inputSchema: {
      type: "object",
      properties: {
        path: {
          type: "string",
          description: `Page path, e.g. "${home}/pricing" or "pricing".`,
        },
      },
      required: ["path"],
      additionalProperties: false,
    },
    annotations: { readOnlyHint: true },
    execute: async (input) => {
      const path =
        typeof input.path === "string"
          ? mirrorPathFor(input.path, country, locale)
          : null
      if (!path) {
        return `Not a readable page in this market. Pass a path under ${home}, as listed by ahead_site_guide.`
      }
      const text = await fetchText(path)
      return text.length > MAX_PAGE_CHARS
        ? `${text.slice(0, MAX_PAGE_CHARS)}\n\n[Truncated at ${MAX_PAGE_CHARS} characters.]`
        : text
    },
  }

  const bookingLink: SiteTool = {
    name: "ahead_booking_link",
    description: waitlistOnly
      ? "Booking is not open in this market yet. Returns where the visitor can join the waitlist instead."
      : "Returns the link that starts a booking with Ahead Health in this market, the same one the site's main \"Book check-up\" button opens. It books nothing: in Ahead's booking app the visitor chooses what to book, the clinic and the time, and pays there.",
    inputSchema: {
      type: "object",
      properties: {},
      additionalProperties: false,
    },
    annotations: { readOnlyHint: true },
    execute: async () =>
      JSON.stringify(
        waitlistOnly
          ? {
              bookable: false,
              message:
                "Booking is not open in this market yet. The visitor can join the waitlist from the site's homepage.",
              waitlistPage: home,
            }
          : {
              bookable: true,
              url: mainBookingUrl(country, locale, flow),
              message:
                "Open this link for the visitor. Nothing is booked until they choose a time and pay in Ahead's booking app.",
            }
      ),
  }

  return [siteGuide, readPage, bookingLink]
}
